import { Container, Tab, TabList, TabPanel, TabPanels, Tabs, Text, Link } from '@chakra-ui/react';
import { StepTemplate } from './StepTemplate';
import { TEMPLATE } from '@/shared/constants';
import { useNavigate, useParams } from 'react-router-dom';
import { AppBreadcrumb } from '../navbar/AppBreadcrumb';
import React from 'react';
import { runtimeConfig } from '../services/RuntimeConfig';
import { VersionInfo } from './VersionInfo';
import { AdminPageTab } from './AdminPageTab';
import { EmailPageTemplates } from './EmailPageTemplates';
import { AdminPageTemplates } from './AdminPageTemplates';
import { SvgPublishComponent } from 'svgpublish-react';
import { AuthService } from '../services/AuthService';

import workflow from '/workflow-one-step.svg?raw';
import { ErrorMessage } from '../components/ErrorMessage';
import { AppContext } from '../comments/AppContext';
import { AppLinkBar } from '../navbar/AppLinkBar';

export const AdminPage = () => {

  const { isAuthenticated } = React.useContext(AppContext);

  const tabs = React.useMemo(() => [
    { label: 'About', url: '/admin/about' },
    ...(AuthService.isAdmin ? [
      { label: 'Workflow Map', url: '/admin/workflow' },
      { label: 'Step Templates', url: '/admin/template' },
      { label: 'Step Templates (old)', url: '/admin/template_old' },
      { label: 'Page Templates', url: '/admin/page' },
      { label: 'Email Templates', url: '/admin/email' },
      { label: 'Email Templates (old)', url: '/admin/email_old' },
    ] : []),
  ], [isAuthenticated]);

  const params = useParams<{ tab: string }>();
  const navigate = useNavigate();

  const tabIndex = Math.max(tabs.findIndex(tab => tab.url.includes(`/admin/${params.tab}`)), 0);

  const onTabChange = (index: number) => {
    navigate(tabs[index].url);
  }

  const breadcrumb = React.useMemo(() => [
    { href: '/', label: 'Home' },
    { href: `/admin`, label: 'Admin' },
    ...(tabIndex > 0 ? [{ href: `${tabs[tabIndex].url}`, label: tabs[tabIndex].label }] : []),
  ], [tabIndex]);

  const jsonTemplateIdsV1 = Object
    .values(TEMPLATE)
    .filter(id => !id.startsWith("V2") && id.endsWith('.json'))
    .sort();

  const jsonTemplateIdsV2 = Object
    .values(TEMPLATE)
    .filter(id => id.startsWith("V2") && id.endsWith('.json'))
    .sort();

  const pageTemplateIds = Object
    .values(TEMPLATE)
    .filter(id => id.startsWith('PAGE_') && !id.endsWith('.json'))
    .sort();

  const emailTemplateIdsV2 = Object
    .values(TEMPLATE)
    .filter(id => !id.startsWith('PAGE_') && !id.endsWith('.json') && id.startsWith('V2_'))
    .sort();

  const emailTemplateIdsV1 = Object
    .values(TEMPLATE)
    .filter(id => !id.startsWith('PAGE_') && !id.endsWith('.json') && !id.startsWith('V2_'))
    .sort();

  const getSvgContent = async () => {
    return workflow.replace(/http:\/\/localhost\//g, runtimeConfig.redirectUri)
  }

  const [errorMessage, setErrorMessage] = React.useState<any>();

  React.useEffect(() => {
    if (isAuthenticated === false) {
      setErrorMessage({ name: 'AuthError', title: 'Please login as an administrator to access this area.' });
    } else if (isAuthenticated === true) {
      setErrorMessage(undefined);
    }
  }, [isAuthenticated]);

  return <>
    <AppBreadcrumb breadcrumb={breadcrumb} />
    <AppLinkBar />
    <Container mt={100} py={{ base: 4 }} px={{ base: 1, md: 8 }}>
      <ErrorMessage error={errorMessage} />
    </Container>
    <Tabs minH='50vh' index={tabIndex} onChange={onTabChange}>
      <Container>
        <TabList>
          {tabs.map((tab, i) => <Tab key={i}>{tab.label}</Tab>)}
        </TabList>
      </Container>

      <TabPanels>
        <TabPanel>
          <Container>
            <AdminPageTab title='Release Information'
              description={<>
                <Text>The poral is deployed automatically from Azure DevOps.</Text>
                <Text>The version information below refers to the <Link target='_blank' href='https://dev.azure.com/eiturbanmobility/EIT%20Label%20Platform/_build' >Azure DevOps build</Link></Text>
              </>}>
              <VersionInfo />
            </AdminPageTab>
          </Container>
        </TabPanel>
        <TabPanel>
          <SvgPublishComponent
            url="/workflow-one-step.svg"
            onGetContent={getSvgContent}
            enablePan
            enableZoom
            enableFollowHyperlinks
            enableHover
            enableBoxSelection
            selectionMode='normal'
            style={{ border: '1px solid #ccc', display: 'flex', flexGrow: 1 }}
            width='100%'
            height='75vh'
          />
        </TabPanel>
        <TabPanel>
          <Container>
            <AdminPageTab title='Step Templates (V2)'
              description={<>
                <Text>Here you can modify the questionnaire templates (V2).</Text>
                <Text>If you modify a template the changes will be reflected in the new course submissions.</Text>
              </>}>
              {jsonTemplateIdsV2.map(templateId => (<StepTemplate key={templateId} fileName={templateId} />))}
            </AdminPageTab>
          </Container>
        </TabPanel>
        <TabPanel>
          <Container>
            <AdminPageTab title='Step Templates (V1)'
              description={<>
                <Text>Here you can modify the questionnaire templates (V1).</Text>
                <Text>If you modify a template the changes will be reflected in the new course submissions.</Text>
              </>}>
              {jsonTemplateIdsV1.map(templateId => (<StepTemplate key={templateId} fileName={templateId} />))}
            </AdminPageTab>
          </Container>
        </TabPanel>
        <TabPanel>
          <Container>
            <AdminPageTab title='Page Templates'
              description={<>
                <Text>Here you can modify the page templates.</Text>
              </>}>
              <AdminPageTemplates buildinTemplateIds={pageTemplateIds} />
            </AdminPageTab>
          </Container>
        </TabPanel>
        <TabPanel>
          <Container>
            <AdminPageTab title='Email Notification Templates'
              description={<>
                <Text>Here you can modify the email templates.</Text>
                <Text>The changes will be reflected immediately when sending the next email (in effect in about 5 minutes).</Text>
              </>}>
              <EmailPageTemplates textTemplateIds={emailTemplateIdsV2} />
            </AdminPageTab>
          </Container>
        </TabPanel>
        <TabPanel>
          <Container>
            <AdminPageTab title='Email Notification Templates (V1)'
              description={<>
                <Text>Here you can modify the email templates (V1).</Text>
                <Text>The changes will be reflected immediately when sending the next email (in effect in about 5 minutes).</Text>
              </>}>
              <EmailPageTemplates textTemplateIds={emailTemplateIdsV1} />
            </AdminPageTab>
          </Container>
        </TabPanel>
      </TabPanels>
    </Tabs>
  </>
}